import { useCallback, useMemo, useState } from 'react';
import { toZoned } from '@internationalized/date';
import { getCaseOrTime } from '@lp/features/RoutePlanner/utils';
import {
  ArrivalHubSettingsType,
  DeploymentHubSettingsType,
  StartLocationSettingsType,
  SuggestedSettingsEnum,
  SuggestedSettingsKeyValueType,
  SuggestedSettingsType,
} from '@lp/features/RoutePlanner/types';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { RouteTypeEnum } from '@/api/apis/route';
import { StrictMap } from '@/utils/helpers';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import {
  convertSettingsListToMap,
  getDefaultSuggestedSettings,
  getDisabledRouteTypes,
  getSettingsPreviousValue,
  getSettingsDefaultValue,
  getSettingsListByRouteType,
  getRestrictedStartLocationIds,
} from './utils';

export const useSuggestedSettings = (currentSettings?: SuggestedSettingsType[]) => {
  const {
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    serviceType,
    opoRequest,
    reAllocationCase,
    donorHospitalDetails,
    txCenterDetails,
  } = useBaseCaseDetails();
  const { legs } = useRouteDetails(RouteTypeEnum.Main);
  const caseOrTime = useMemo(
    () => getCaseOrTime(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone),
    [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone],
  );
  const primaryLocationIds = useMemo(
    () => ({ donorHospitalId: donorHospitalDetails?.id, txCenterId: txCenterDetails?.id }),
    [donorHospitalDetails?.id, txCenterDetails?.id],
  );
  const disabledRouteTypes = useMemo(() => {
    return getDisabledRouteTypes(legs, reAllocationCase, txCenterDetails, caseOrTime, primaryLocationIds);
  }, [legs, reAllocationCase, txCenterDetails, caseOrTime, primaryLocationIds]);

  const [settingsMap, setSettingsMap] = useState<StrictMap<SuggestedSettingsKeyValueType>>(() => {
    const restoredSettings = currentSettings?.length ? convertSettingsListToMap(currentSettings) : null;
    const restoredRouteType = restoredSettings?.get(SuggestedSettingsEnum.RouteType);
    const isRestoredRouteTypeDisabled = !!restoredRouteType && !!disabledRouteTypes[restoredRouteType];

    if (restoredSettings && !isRestoredRouteTypeDisabled) {
      return restoredSettings;
    }

    return getDefaultSuggestedSettings(
      legs,
      serviceType,
      opoRequest,
      reAllocationCase,
      txCenterDetails,
      caseOrTime,
      primaryLocationIds,
    );
  });

  const handleRouteTypeChange = useCallback(
    (type: RouteTypeSettingsEnum) => {
      setSettingsMap((prev) => {
        const newSettingsMap = new StrictMap<SuggestedSettingsKeyValueType>();
        const newSettingsItem = getSettingsListByRouteType(type, serviceType);
        const restrictedStartLocationIds = getRestrictedStartLocationIds(
          type,
          donorHospitalDetails?.id,
          txCenterDetails?.id,
        );

        newSettingsItem.forEach((key) => {
          if (!prev.has(key)) {
            newSettingsMap.set(key, getSettingsDefaultValue(key, caseOrTime));
          } else {
            newSettingsMap.set(key, getSettingsPreviousValue(prev, key, caseOrTime, restrictedStartLocationIds));
          }
        });

        newSettingsMap.set(SuggestedSettingsEnum.RouteType, type);

        return newSettingsMap;
      });
    },
    [caseOrTime, serviceType, donorHospitalDetails, txCenterDetails],
  );

  const handleSettingsChange = useCallback((data: SuggestedSettingsType) => {
    setSettingsMap((prev) => {
      const newSettingsMap = new StrictMap<SuggestedSettingsKeyValueType>(prev);
      newSettingsMap.set(data.type, data.value);

      return newSettingsMap;
    });
  }, []);

  const handleStartLocationChange = useCallback((data: StartLocationSettingsType) => {
    setSettingsMap((prev) => {
      const newSettingsMap = new StrictMap<SuggestedSettingsKeyValueType>(prev);
      newSettingsMap.set(data.type, data.value);

      const timezone = data.value?.location?.timezone;
      const startTime = prev.get(SuggestedSettingsEnum.StartTime);

      if (timezone && startTime) {
        newSettingsMap.set(SuggestedSettingsEnum.StartTime, toZoned(startTime, timezone));
      }

      return newSettingsMap;
    });
  }, []);

  const handleDeploymentHubChange = useCallback((data: DeploymentHubSettingsType | ArrivalHubSettingsType) => {
    setSettingsMap((prev) => {
      const newSettingsMap = new StrictMap<SuggestedSettingsKeyValueType>(prev);
      newSettingsMap.set(data.type, data.value);

      if (data.value) {
        newSettingsMap.set(SuggestedSettingsEnum.CS, null);
      }

      return newSettingsMap;
    });
  }, []);

  const handleSettingsReset = useCallback(() => {
    setSettingsMap(() => {
      return getDefaultSuggestedSettings(
        legs,
        serviceType,
        opoRequest,
        reAllocationCase,
        txCenterDetails,
        caseOrTime,
        primaryLocationIds,
      );
    });
  }, [caseOrTime, serviceType, legs, opoRequest, reAllocationCase, txCenterDetails, primaryLocationIds]);

  const settingsList = useMemo(() => {
    return Array.from(settingsMap.entries()).map(([key, value]) => ({
      type: key,
      value,
    })) as SuggestedSettingsType[];
  }, [settingsMap]);

  return {
    caseOrTime,
    disabledRouteTypes,
    settingsList,
    handleRouteTypeChange,
    handleStartLocationChange,
    handleDeploymentHubChange,
    handleSettingsChange,
    handleSettingsReset,
  };
};
